import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getStoredToken } from '../../utils/tokenStorage';

interface SharingPolicy {
  tenant_name: string;
  admin_name?: string | null;
  admin_email?: string | null;
  allow_external_sharing: boolean;
  allowed_share_domains: string[];
  internal_domains: string[];
  domain_restricted: boolean;
  require_external_approval: boolean;
  require_otp: boolean;
  watermarking_enabled: boolean;
  default_expiry_days?: number | null;
}

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  fileName: string;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ isOpen, onClose, fileId, fileName }) => {
  const { branding } = useAuth();
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('view');
  const [expiresDays, setExpiresDays] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [shareResult, setShareResult] = useState<{ share_url: string; status: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [policy, setPolicy] = useState<SharingPolicy | null>(null);
  const [policyLoaded, setPolicyLoaded] = useState(false);

  // Fetch tenant policy on open to get default expiry and domain info
  useEffect(() => {
    if (!isOpen) {
      setPolicyLoaded(false);
      return;
    }
    const token = getStoredToken();
    fetch('/api/v1/sharing-policy', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setPolicy(data);
          // Apply default expiry from tenant policy
          if (data.default_expiry_days && data.default_expiry_days > 0) {
            setExpiresDays(data.default_expiry_days);
          }
        }
        setPolicyLoaded(true);
      })
      .catch(() => { setPolicyLoaded(true); });
  }, [isOpen]);

  if (!isOpen) return null;

  const recipientDomain = email.includes('@') ? email.split('@')[1]?.trim().toLowerCase() : '';
  const isInternal = Boolean(recipientDomain && policy?.internal_domains?.some(
    d => recipientDomain === d.toLowerCase() || recipientDomain.endsWith('.' + d.toLowerCase())
  ));
  const isExternal = Boolean(recipientDomain && policyLoaded && !isInternal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = getStoredToken();
      const res = await fetch(`/api/v1/files/${fileId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          permission,
          expires_days: expiresDays,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to share');
        setSubmitting(false);
        return;
      }

      const data = await res.json();
      setShareResult({ share_url: data.share_url, status: data.status });
    } catch {
      setError('Failed to share file');
    }
    setSubmitting(false);
  };

  const handleCopy = async () => {
    if (shareResult?.share_url) {
      await navigator.clipboard.writeText(shareResult.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPermission('view');
    setExpiresDays(null);
    setError('');
    setShareResult(null);
    setCopied(false);
    setPolicy(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 sm:p-6" onClick={handleClose}>
      <div
        className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto p-5 sm:p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-white">Share file</h3>
          <p className="mt-1 text-sm text-slate-300 break-words [overflow-wrap:anywhere]" title={fileName}>
            {fileName}
          </p>
        </div>

        {!shareResult ? (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-teal-500 outline-none"
                placeholder="colleague@company.com"
                required
              />
              {/* Domain indicator */}
              {recipientDomain && (() => {
                if (isInternal) {
                  return (
                    <p className="text-xs text-teal-400 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                      Internal domain
                    </p>
                  );
                }
                if (isExternal) {
                  return (
                    <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      {!policy?.allow_external_sharing
                        ? 'External sharing is disabled'
                        : policy?.require_external_approval
                          ? `External recipient — approval will be sent to ${policy.admin_name || policy.admin_email || 'your administrator'}`
                          : 'External recipient'}
                    </p>
                  );
                }
                return null;
              })()}
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-slate-400 mb-1">Permission</label>
                <select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-teal-500 outline-none"
                >
                  <option value="view">View only</option>
                  <option value="comment">Comment</option>
                  <option value="edit">Edit</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm text-slate-400 mb-1">Expires</label>
                <select
                  value={expiresDays ?? ''}
                  onChange={(e) => setExpiresDays(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-teal-500 outline-none"
                >
                  {/* If tenant has a default expiry, "Never" is not available */}
                  {(!policy?.default_expiry_days || policy.default_expiry_days <= 0) && (
                    <option value="">Never</option>
                  )}
                  {[7, 30, 60, 90].filter(d => !policy?.default_expiry_days || d <= policy.default_expiry_days).map(d => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </select>
                {policy?.default_expiry_days && policy.default_expiry_days > 0 && (
                  <p className="text-xs text-slate-500 mt-1">Max: {policy.default_expiry_days} days (org policy)</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !policyLoaded || (isExternal && !policy?.allow_external_sharing)}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
                }}
              >
                {submitting
                  ? 'Submitting...'
                  : !policyLoaded
                    ? 'Loading...'
                    : isExternal && policy?.require_external_approval
                      ? 'Request approval'
                      : 'Share'}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 space-y-4">
            {shareResult.status === 'pending_approval' ? (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-3 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Sent to administrator for approval</span>
                </div>
                <p className="text-amber-200/80 text-xs mt-1">
                  External shares require administrator approval per your organisation's policy. You'll be notified once it's reviewed.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-teal-500/10 border border-teal-500/30 text-teal-300 px-3 py-3 rounded-lg text-sm">
                  {shareResult.status === 'existing_user'
                    ? 'Shared successfully! They can access it now.'
                    : 'Share link created. They\'ll be invited to create an account.'}
                </div>

                <div className="flex gap-2">
                  <input
                    readOnly
                    value={shareResult.share_url}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 text-sm font-medium text-white bg-white/10 border border-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareDialog;
